// Centralized social auth helpers for web + Capacitor (Android/iOS).
//
// Mobile rules:
//   - Google OAuth and Apple web OAuth MUST open in an external browser
//     (Chrome Custom Tab on Android, SFSafariViewController on iOS).
//     Google blocks embedded WebViews (disallowed_useragent).
//   - iOS uses Native Sign In with Apple (@capacitor-community/apple-sign-in).
//     Its JWT has aud = iOS bundle id (com.storyfyc.app). Backend accepts both.
//   - Backend redirects mobile flows to `com.storyfyc.app://auth/callback?...`.
//     The deep-link listener in App.js consumes that URL.
//
// Web rules:
//   - window.location.href redirect (legacy, unchanged).
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const currentPlatform = () => {
  try {
    return Capacitor.getPlatform() || 'web';
  } catch {
    return 'web';
  }
};

// Resolves a human-readable error string from an axios/fetch error.
export const readableAuthError = (err, fallback) => {
  if (!err) return fallback || 'Unknown error';
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (err?.message) return err.message;
  try {
    return JSON.stringify(detail || err);
  } catch {
    return fallback || 'Unknown error';
  }
};

// Google: ask backend for the auth URL (with platform in state), then open it.
// Mobile -> external browser (deep-link return handled in App.js).
// Web    -> window.location redirect (unchanged).
export const startGoogleSignIn = async () => {
  const native = isNative();
  const platform = native ? currentPlatform() : 'web';
  const res = await axios.get(`${API}/auth/google/login?platform=${platform}`);
  const authUrl = res?.data?.auth_url;
  if (!authUrl) throw new Error('Google auth URL missing from server response');
  if (native) {
    await Browser.open({ url: authUrl, presentationStyle: 'popover' });
    return { opened: 'external' };
  }
  window.location.href = authUrl;
  return { opened: 'web' };
};

// Apple web flow (used on Web + Android).
// iOS uses the native flow (startNativeAppleSignIn below).
export const startAppleWebSignIn = async () => {
  const native = isNative();
  const platform = native ? currentPlatform() : 'web';
  // Force storyfyc.com canonical domain on web to keep Apple return URL consistent.
  if (!native && typeof window !== 'undefined' && window.location.hostname === 'www.storyfyc.com') {
    window.location.href = 'https://storyfyc.com/login';
    return { opened: 'web-redirect' };
  }
  const clientId = 'com.storyfyc.web'; // Apple Services ID (web form_post)
  // Backend callback (ALWAYS storyfyc.com — only host allowed by Apple portal).
  const redirectUri = 'https://storyfyc.com/api/auth/apple/callback';
  const scope = 'email name';
  const state = `storyfyc-${Date.now()}-platform=${platform}`;
  const nonce = `nonce-${Date.now()}`;
  const authUrl =
    `https://appleid.apple.com/auth/authorize` +
    `?client_id=${clientId}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&response_type=${encodeURIComponent('code id_token')}` +
    `&scope=${encodeURIComponent(scope)}` +
    `&state=${state}` +
    `&response_mode=form_post` +
    `&nonce=${nonce}`;
  if (native) {
    await Browser.open({ url: authUrl, presentationStyle: 'popover' });
    return { opened: 'external' };
  }
  window.location.href = authUrl;
  return { opened: 'web' };
};

// iOS native Sign In with Apple (lazy-loaded to avoid web bundle bloat).
export const startNativeAppleSignIn = async () => {
  if (!isNative() || currentPlatform() !== 'ios') {
    throw new Error('Native Apple Sign In is only available on iOS');
  }
  const mod = await import('@capacitor-community/apple-sign-in');
  const SignInWithApple = mod.SignInWithApple || mod.default?.SignInWithApple || mod.default;
  if (!SignInWithApple || typeof SignInWithApple.authorize !== 'function') {
    throw new Error('Apple Sign In plugin not available');
  }
  const response = await SignInWithApple.authorize({
    clientId: 'com.storyfyc.app', // iOS bundle id
    redirectURI: 'https://storyfyc.com/api/auth/apple/callback', // required by plugin, not used in native flow
    scopes: 'email name',
    state: `ios-native-${Date.now()}`,
    nonce: `nonce-${Date.now()}`,
  });
  const r = response?.response || response || {};
  const identityToken = r.identityToken;
  if (!identityToken) throw new Error('Apple did not return an identity token');
  // Send to backend for verification + session creation.
  const userPayload = {};
  if (r.email) userPayload.email = r.email;
  const given = r.givenName || r.firstName;
  const family = r.familyName || r.lastName;
  if (given || family) userPayload.name = `${given || ''} ${family || ''}`.trim();
  const { data } = await axios.post(`${API}/auth/apple/native`, {
    identity_token: identityToken,
    user: userPayload,
  });
  return data; // { access_token, user, provider: 'apple' }
};

// Returns true on iOS (both simulator and device) so the UI can pick native path.
export const isIOSNative = () => isNative() && currentPlatform() === 'ios';

// Convenience: persist a token like the email login flow and navigate home.
export const persistTokenAndReload = (accessToken, userId) => {
  if (!accessToken) return;
  try {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('hasSession', 'true');
    if (userId) localStorage.setItem('current_user_id', userId);
  } catch {
    /* ignore storage errors */
  }
  // Full reload so App.js re-mounts with authenticated state.
  window.location.href = '/';
};
